import { makeAwsSignedRequest } from '../ApiService';

const BATCHER_BASE = 'batcher-api';
const FUNCTION_ID = 'com.amazon.sherlockbatcherapifunctions.functions.OpenSearchTranslateFunction';

export interface TranslateJobParams {
    sourceField: string;
    resourceType: string;
    targetFields: string;
    queryType: string;
}

export interface JobResult {
    status: 'SUCCEEDED' | 'FAILED' | 'RUNNING';
    downloadUrl?: string;
}

function extractFileName(presignedUrl: string): string {
    const url = new URL(presignedUrl);
    return url.pathname.replace(/^\//, '');
}

export const TranslateService = {
    getPresignedUrl: (stage: string, realm: string): Promise<string> =>
        makeAwsSignedRequest(stage, realm, `${BATCHER_BASE}/presigned-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'upload', fileName: 'translate-input.csv' }),
            retry: false,
        }),

    uploadCsv: async (presignedUrl: string, csvContent: string): Promise<Response> => {
        const resp = await fetch(presignedUrl, {
            method: 'PUT',
            body: csvContent,
            headers: { 'Content-Type': 'text/csv' },
        });
        if (!resp.ok) {
            throw new Error(`CSV upload failed: ${resp.status} ${resp.statusText}`);
        }
        return resp;
    },

    submitJob: (stage: string, realm: string, fileName: string, params: TranslateJobParams): Promise<string> =>
        makeAwsSignedRequest(stage, realm, `${BATCHER_BASE}/jobs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                fileName,
                functionChain: [{
                    functionIdentifier: FUNCTION_ID,
                    additionalArguments: params,
                }],
            }),
            retry: false,
        }).then((resp: any) => resp.jobId || resp),

    getJobResult: (stage: string, realm: string, jobId: string): Promise<JobResult> =>
        makeAwsSignedRequest(stage, realm, `${BATCHER_BASE}/jobs/${jobId}`),

    pollUntilComplete: async (
        stage: string, realm: string, jobId: string,
        maxAttempts = 30, intervalMs = 3000
    ): Promise<JobResult> => {
        for (let i = 0; i < maxAttempts; i++) {
            const result = await TranslateService.getJobResult(stage, realm, jobId);
            if (result.status === 'SUCCEEDED' || result.status === 'FAILED') return result;
            await new Promise(r => setTimeout(r, intervalMs));
        }
        throw new Error('Job timed out after 90 seconds');
    },

    downloadResult: async (url: string): Promise<string> => {
        const resp = await fetch(url);
        if (!resp.ok) {
            throw new Error(`Result download failed: ${resp.status} ${resp.statusText}`);
        }
        return resp.text();
    },

    extractFileName,
};
